import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import { CheckboxInput, Typography } from "@/components/atoms";
import { formatStatusLabel } from "../../../MyDrivers/MyDrivers/helpers";
import { ActiveStatuses, ArchivedStatuses, PendingStatuses, SuspendedStatuses } from "../../../MyDrivers/MyDrivers/types";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterPopover } from "../common/FilterPopover";
import { useDriversFilters } from "../useDriversFilters";

const schema = z.object({
  status: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const formatFilterDisplay = (status: (string | null)[] | null | undefined) => {
  if (!status) return undefined;
  const labels = [...new Set(status.filter(Boolean).map((s) => capitalize(formatStatusLabel(s!))))];
  return labels.length > 0 ? labels.join(", ") : undefined;
};

export const StatusFilter = () => {
  const {
    filters: { status },
    setFilter,
  } = useDriversFilters();

  const value = useMemo(() => formatFilterDisplay(status), [status]);

  const onClose = () => setFilter("status", undefined);

  return (
    <FilterPopover name="status" value={value} label="Status" onClose={onClose}>
      <StatusFilterForm />
    </FilterPopover>
  );
};

interface StatusFilterDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const StatusFilterDrawer = ({ open, onOpenChange }: StatusFilterDrawerProps) => {
  const {
    filters: { status },
    setFilter,
  } = useDriversFilters();

  const value = useMemo(() => formatFilterDisplay(status), [status]);

  const onClose = () => setFilter("status", undefined);

  return (
    <FilterDrawer open={open} onOpenChange={onOpenChange} value={value} label="Status" onClose={onClose}>
      <StatusFilterForm />
    </FilterDrawer>
  );
};

const StatusFilterForm = () => {
  const { pathname } = useLocation();
  const { setFilter, filters } = useDriversFilters();
  const isArchivedDrivers = pathname.includes("/drivers/archived");

  // Group raw statuses by display label so one checkbox controls multiple raw values
  const groupedOptions = useMemo(() => {
    const statusList = isArchivedDrivers ? ArchivedStatuses : [...ActiveStatuses, ...PendingStatuses, ...SuspendedStatuses];
    const groups: Array<{ label: string; rawValues: string[] }> = [];
    for (const status of statusList) {
      const label = formatStatusLabel(status);
      const existing = groups.find((g) => g.label === label);
      if (existing) {
        existing.rawValues.push(status);
      } else {
        groups.push({ label, rawValues: [status] });
      }
    }
    return groups;
  }, [isArchivedDrivers]);

  const allRawValues = useMemo(() => groupedOptions.flatMap((g) => g.rawValues), [groupedOptions]);

  const { control, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { status: (filters.status as string[]) ?? [] },
  });

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        setFilter("status", allRawValues);
        setValue("status", allRawValues);
      } else {
        setFilter("status", null);
        setValue("status", []);
      }
    },
    [setFilter, setValue, allRawValues]
  );

  const isAllStatusesSelected = useMemo(() => {
    if (!filters.status) return false;
    return allRawValues.every((v) => filters.status!.includes(v));
  }, [filters.status, allRawValues]);

  return (
    <div className="contents">
      <Typography className="mb-4">Status</Typography>
      <CheckboxInput label="All Statuses" onChange={handleCheckAll} variant="check" checked={isAllStatusesSelected} />
      <div className="mt-4 grid gap-4 ">
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <>
              {groupedOptions.map((group) => {
                const isChecked = group.rawValues.every((v) => field.value.includes(v));

                return (
                  <CheckboxInput
                    className="capitalize"
                    key={group.label}
                    label={group.label}
                    value={group.label}
                    onChange={() => {
                      if (!isChecked) {
                        const newStatus = [...new Set([...field.value, ...group.rawValues])];
                        field.onChange(newStatus);
                        setFilter("status", newStatus.length > 0 ? newStatus : null);
                      } else {
                        const newStatus = field.value.filter((i) => !group.rawValues.includes(i));
                        field.onChange(newStatus);
                        setFilter("status", newStatus.length > 0 ? newStatus : null);
                      }
                    }}
                    variant="check"
                    checked={isChecked}
                  />
                );
              })}
            </>
          )}
        />
      </div>
    </div>
  );
};
